// frontend/src/App.js - Fixed Enter key handling

import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import './App.css';

const SOCKET_URL = window.location.origin;

const safeTrim = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileIcon = (mimetype) => {
  if (!mimetype) return '📄';
  if (mimetype.startsWith('image/')) return '🖼️';
  if (mimetype === 'application/pdf') return '📕';
  if (mimetype.startsWith('text/')) return '📝';
  if (mimetype === 'application/json') return '📊';
  return '📎';
};

function SignInButton() {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
    }
  };

  return (
    <button onClick={handleLogin} disabled={isLoading} className="ms-signin-button">
      <svg width="20" height="20" viewBox="0 0 23 23" fill="none">
        <path d="M11 11H0V0H11V11Z" fill="#F25022"/>
        <path d="M23 11H12V0H23V11Z" fill="#7FBA00"/>
        <path d="M11 23H0V12H11V23Z" fill="#00A4EF"/>
        <path d="M23 23H12V12H23V23Z" fill="#FFB900"/>
      </svg>
      {isLoading ? 'Redirecting...' : 'Sign in with Microsoft'}
    </button>
  );
}

function App() {
  const { accounts, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  
  const [socket, setSocket] = useState(null);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isClaudeThinking, setIsClaudeThinking] = useState(false);
  const [showNewRoomInput, setShowNewRoomInput] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const username = accounts[0]?.name || accounts[0]?.username?.split('@')[0] || '';
  const userEmail = accounts[0]?.username || '';

  useEffect(() => {
    if (!isAuthenticated) return;

    const newSocket = io(SOCKET_URL);
    setSocket(newSocket);

    newSocket.on('connect', () => {
      setIsConnected(true);
      fetchRooms();
    });
    
    newSocket.on('disconnect', () => setIsConnected(false));

    newSocket.on('room-joined', ({ room, messages: roomMessages }) => {
      setCurrentRoom(room);
      setMessages(roomMessages || []);
      setParticipants(room.participants || []);
    });

    newSocket.on('message', (message) => {
      setMessages(prev => [...prev, message]);
      fetchRooms();
    });

    newSocket.on('participants-updated', (updatedParticipants) => {
      setParticipants(updatedParticipants || []);
    });

    newSocket.on('user-joined', ({ username: joinedUser }) => {
      setMessages(prev => [...prev, {
        type: 'system',
        text: `${joinedUser} joined the room`,
        timestamp: new Date(),
        id: Date.now()
      }]);
    });

    newSocket.on('user-left', ({ username: leftUser }) => {
      setMessages(prev => [...prev, {
        type: 'system',
        text: `${leftUser} left the room`,
        timestamp: new Date(),
        id: Date.now()
      }]);
    });

    newSocket.on('user-typing', ({ username: typingUser, isTyping }) => {
      setTypingUsers(prev => {
        if (isTyping) {
          return [...prev.filter(u => u !== typingUser), typingUser];
        } else {
          return prev.filter(u => u !== typingUser);
        }
      });
    });

    newSocket.on('claude-thinking', ({ isThinking }) => {
      setIsClaudeThinking(isThinking);
    });

    return () => {
      newSocket.close();
    };
  }, [isAuthenticated, username]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isClaudeThinking, typingUsers]);

  const fetchRooms = async () => {
    try {
      const response = await fetch(`${SOCKET_URL}/api/rooms`);
      const rooms = await response.json();
      setAvailableRooms(rooms || []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const createRoom = async (roomName) => {
    try {
      const response = await fetch(`${SOCKET_URL}/api/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: roomName, createdBy: username })
      });
      const newRoom = await response.json();
      await fetchRooms();
      return newRoom.room_id || newRoom.id;
    } catch (error) {
      console.error('Error creating room:', error);
      return null;
    }
  };

  const joinRoom = (roomIdToJoin) => {
    if (!username || !roomIdToJoin || !socket || !isConnected) return;
    
    socket.emit('join-room', { 
      roomId: String(roomIdToJoin), 
      username: username 
    });
  };

  // FIXED: New room creation handler
  const handleCreateRoom = async () => {
    const name = safeTrim(newRoomName);
    if (!name) {
      setShowNewRoomInput(false);
      return;
    }
    
    const newRoomId = await createRoom(name);
    if (newRoomId) {
      joinRoom(newRoomId);
    }
    setNewRoomName('');
    setShowNewRoomInput(false);
  };

  // FIXED: New room key handler
  const handleNewRoomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleCreateRoom();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setNewRoomName('');
      setShowNewRoomInput(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 32 * 1024 * 1024) {
      alert('File too large. Maximum size is 32MB.');
      return;
    }
    
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'text/plain', 'text/csv', 'text/markdown',
      'application/json'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      alert('File type not supported.\n\nAllowed: PDF, images, text files');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${SOCKET_URL}/api/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setAttachedFile(data);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file: ' + error.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = () => setAttachedFile(null);

  // FIXED: Send message - more robust
  const handleSendMessage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const text = safeTrim(messageText);
    
    if (!socket || !currentRoom) return;
    if (!text && !attachedFile) return;
    
    const messageContent = text || 
      (attachedFile ? `Please analyze this file: ${attachedFile.originalName}` : '');
    
    socket.emit('send-message', {
      roomId: currentRoom.id,
      text: messageContent,
      username: username,
      attachment: attachedFile
    });
    
    setMessageText('');
    setAttachedFile(null);
    handleTyping(false);
  };

  // FIXED: Handle Enter key in message input
  const handleMessageKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTyping = (typing) => {
    if (socket && currentRoom && typing !== isTyping) {
      setIsTyping(typing);
      socket.emit('typing', {
        roomId: currentRoom.id,
        username: username,
        isTyping: typing
      });
    }

    if (typing) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        handleTyping(false);
      }, 2000);
    }
  };

  const handleSignOut = async () => {
    try {
      await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
    } catch (e) {
      sessionStorage.clear();
      localStorage.clear();
      window.location.reload();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      <UnauthenticatedTemplate>
        <div className="login-container">
          <div className="login-card">
            <div className="logo">
              <img 
                src="/images/logo.png" 
                alt="Carelync Logo" 
                className="logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
              <div className="logo-fallback" style={{ display: 'none' }}>C</div>
              <h1>Carelync AI</h1>
              <p>Sign in with your Microsoft account to continue</p>
            </div>
            <SignInButton />
          </div>
        </div>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <div className="app-container">
          {/* LEFT SIDEBAR */}
          <aside className="sidebar">
            <div className="sidebar-logo">
              <img 
                src="/images/logo.png" 
                alt="Carelync" 
                className="sidebar-logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="sidebar-logo-fallback" style={{ display: 'none' }}>C</div>
              <span className="sidebar-logo-text">Carelync AI</span>
            </div>

            <button 
              type="button"
              className="new-room-button"
              onClick={() => setShowNewRoomInput(!showNewRoomInput)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              New Chat
            </button>

            {showNewRoomInput && (
              <div className="new-room-form">
                <input
                  type="text"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  onKeyDown={handleNewRoomKeyDown}
                  placeholder="Room name (Enter to create)"
                  autoFocus
                />
              </div>
            )}

            <div className="sidebar-section">
              <h3 className="sidebar-section-title">Chats</h3>
              <div className="rooms-list">
                {availableRooms.length === 0 ? (
                  <div className="empty-state">No chats yet</div>
                ) : (
                  availableRooms.map((room, index) => {
                    const roomIdValue = room.room_id || room.id || room.roomId;
                    const roomName = room.name || `Room ${roomIdValue}`;
                    const isActive = currentRoom && currentRoom.id === roomIdValue;
                    
                    return (
                      <button
                        key={roomIdValue || index}
                        type="button"
                        className={`room-item ${isActive ? 'active' : ''}`}
                        onClick={() => joinRoom(roomIdValue)}
                      >
                        <div className="room-item-content">
                          <div className="room-item-name">{roomName}</div>
                          <div className="room-item-meta">
                            {room.active_participants || 0} online
                            {room.total_messages > 0 && ` · ${room.total_messages} msgs`}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {currentRoom && participants.length > 0 && (
              <div className="sidebar-section">
                <h3 className="sidebar-section-title">Online ({participants.length})</h3>
                <div className="participants-list">
                  {participants.map(participant => (
                    <div key={participant.socketId} className="participant">
                      <div className="participant-avatar">
                        {(participant.username || '?').charAt(0).toUpperCase()}
                      </div>
                      <span>{participant.username}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="sidebar-footer">
              <div className="user-profile">
                <div className="user-avatar">
                  {username.charAt(0).toUpperCase()}
                </div>
                <div className="user-info">
                  <div className="user-name">{username}</div>
                  {userEmail && userEmail !== username && (
                    <div className="user-email">{userEmail}</div>
                  )}
                </div>
              </div>
              <button type="button" onClick={handleSignOut} className="signout-button" title="Sign out">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </div>

            <div className="connection-bar">
              <div className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></div>
              <span>{isConnected ? 'Connected' : 'Connecting...'}</span>
            </div>
          </aside>

          {/* MAIN CONTENT */}
          <main className="main-content">
            {!currentRoom ? (
              <div className="empty-chat-state">
                <img 
                  src="/images/CareLync_FBlogo.png" 
                  alt="Carelync" 
                  className="empty-state-logo"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <h1>Welcome to Carelync AI</h1>
                <p>Select a chat from the sidebar or create a new one to get started</p>
                <button 
                  type="button"
                  className="primary-button"
                  onClick={() => {
  			setShowNewRoomInput(true);
  			setTimeout(() => {
    			const input = document.querySelector('.new-room-form input');
    			if (input) input.focus();
  		   }, 100);
}}
                >
                  Start New Chat
                </button>
              </div>
            ) : (
              <>
                <header className="chat-header">
                  <div className="chat-header-info">
                    <h2>{currentRoom.name}</h2>
                    <span>{participants.length} {participants.length === 1 ? 'participant' : 'participants'}</span>
                  </div>
                </header>

                <div className="messages-container">
                  <div className="messages">
                    {messages.length === 0 && !isClaudeThinking && (
                      <div className="messages-empty">
                        <p>Start the conversation by sending a message below.</p>
                      </div>
                    )}
                    
                    {messages.map((message, index) => (
                      <div 
                        key={message.id || index} 
                        className={`message message-${message.type}`}
                      >
                        {message.type === 'user' && (
                          <>
                            <div className="message-avatar">
                              {(message.username || '?').charAt(0).toUpperCase()}
                            </div>
                            <div className="message-content">
                              <div className="message-header">
                                <span className="message-username">{message.username}</span>
                                <span className="message-time">{formatTime(message.timestamp)}</span>
                              </div>
                              {message.attachment && (
                                <div className="message-attachment">
                                  <span className="file-icon">{getFileIcon(message.attachment.mimetype)}</span>
                                  <div className="file-info">
                                    <div className="file-name">{message.attachment.originalName}</div>
                                    <div className="file-size">{formatFileSize(message.attachment.size)}</div>
                                  </div>
                                </div>
                              )}
                              <div className="message-text">{message.text}</div>
                            </div>
                          </>
                        )}
                        
                        {message.type === 'claude' && (
                          <>
                            <div className="message-avatar claude-avatar">
                              <img 
                                src="/images/logo.png" 
                                alt="AI" 
                                className="claude-avatar-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = 'AI';
                                }}
                              />
                            </div>
                            <div className="message-content">
                              <div className="message-header">
                                <span className="message-username claude-username">Carelync AI</span>
                                <span className="message-time">{formatTime(message.timestamp)}</span>
                              </div>
                              <div className="message-text claude-text">{message.text}</div>
                            </div>
                          </>
                        )}
                        
                        {message.type === 'system' && (
                          <div className="system-message">{message.text}</div>
                        )}
                      </div>
                    ))}
                    
                    {isClaudeThinking && (
                      <div className="message claude-thinking">
                        <div className="message-avatar claude-avatar">
                          <img 
                            src="/images/logo.png" 
                            alt="AI" 
                            className="claude-avatar-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = 'AI';
                            }}
                          />
                        </div>
                        <div className="message-content">
                          <div className="message-header">
                            <span className="message-username claude-username">Carelync AI</span>
                          </div>
                          <div className="claude-thinking-indicator">
                            <div className="typing-dots">
                              <span></span><span></span><span></span>
                            </div>
                            <span className="thinking-text">is thinking...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {typingUsers.length > 0 && (
                      <div className="typing-indicator">
                        <div className="typing-dots">
                          <span></span><span></span><span></span>
                        </div>
                        <span>{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing</span>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {attachedFile && (
                  <div className="attachment-preview-container">
                    <div className="attachment-preview">
                      <span className="file-icon">{getFileIcon(attachedFile.mimetype)}</span>
                      <div className="file-info">
                        <div className="file-name">{attachedFile.originalName}</div>
                        <div className="file-size">{formatFileSize(attachedFile.size)}</div>
                      </div>
                      <button 
                        type="button"
                        onClick={removeAttachment}
                        className="remove-attachment"
                        title="Remove attachment"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                {/* FIXED: No more form wrapping - using button click and Enter key */}
                <div className="message-input-container">
                  <div className="message-form">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.md,.json"
                      style={{ display: 'none' }}
                    />
                    
                    <button 
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="attach-button"
                      disabled={isUploading || attachedFile}
                      title="Attach document"
                    >
                      {isUploading ? (
                        <div className="upload-spinner"></div>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                        </svg>
                      )}
                    </button>
                    
                    <input
                      type="text"
                      value={messageText || ''}
                      onChange={(e) => {
                        setMessageText(e.target.value);
                        handleTyping(e.target.value.length > 0);
                      }}
                      onKeyDown={handleMessageKeyDown}
                      placeholder={attachedFile ? "Add a message about the file..." : "Message Carelync AI..."}
                      className="message-input"
                    />
                    
                    <button 
                      type="button"
                      onClick={handleSendMessage}
                      disabled={!safeTrim(messageText) && !attachedFile}
                      className="send-button"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </AuthenticatedTemplate>
    </>
  );
}

export default App;